// ProductCard.tsx
import React, {
  createContext,
  Ref,
  RefCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { BestSellingProducts } from "@/features/dashboard/features/best-selling-section/types";
import { AspectRatio, Box, Button, Flex, Image, Text } from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import AppLabel from "@/features/ui/app-label";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";

interface CardContextValue {
  showOrderable: boolean;
  product: BestSellingProducts;
}

const CardContext = createContext<CardContextValue | null>(null);

function BestSellingProductCard({
  showOrderable,
  product,
  children,
}: CardContextValue & { children: React.ReactNode }) {
  return (
    <CardContext.Provider value={{ showOrderable, product }}>
      <Box
        minW={"96px"}
        className={"group"}
        padding={"4px"}
        border={"1px solid transparent"}
        borderRadius={"4px"}
        _hover={{
          borderColor: "#1272EF",
          background: "#F7F9FA",
        }}
      >
        {children}
      </Box>
    </CardContext.Provider>
  );
}

function ProductCardImage({ imgRef }: { imgRef: Ref<HTMLImageElement> }) {
  const ctx = useContext(CardContext)!;
  const imgUrl = ctx.product.image_urls ?? [];
  const { navigateToLineSheetPage, navigateToPathPickSearchProductsPage } =
    useRouterHelper();
  const { tTitle } = useI18nHelper();

  return (
    <Box position={"relative"}>
      <AspectRatio ratio={1}>
        <Image
          src={imgUrl[0]}
          alt={ctx.product.sku}
          className="w-full h-auto"
          borderRadius={"4px"}
          height={"96px"}
          width={"100%"}
          objectFit={"cover"}
          border={"1px solid #E5E5E5"}
          ref={imgRef}
          _groupHover={{
            opacity:
              ctx.product.in_valid_line_sheet && ctx.showOrderable ? 0.25 : 1,
          }}
        />
      </AspectRatio>
      <Box
        borderRadius="4px 0px 4px 0px"
        position={"absolute"}
        top={0}
        left={0}
        background={ctx.product.highlight ? "#1272EF" : "#363636"}
        minWidth={"25px"}
        height={"20px"}
        paddingX={"6px"}
      >
        <Text color={"#FFFFFF"} textStyle={"body-sm-bold"} textAlign={"center"}>
          {ctx.product.ranking}
        </Text>
      </Box>
      {(ctx.product.in_valid_line_sheet || ctx.product.in_valid_api_order) &&
        ctx.showOrderable && (
          <Box
            position={"absolute"}
            top={"6px"}
            right={"6px"}
            border={"1px solid #16B41C"}
            background={"#F0FAEF"}
            paddingX={"8px"}
            borderRadius={"2px"}
          >
            <Text textStyle={"body-sm-bold"} color={"#444444"}>
              {tTitle("common:orderable")}
            </Text>
          </Box>
        )}
      {ctx.showOrderable && (
        <Box
          position={"absolute"}
          display={"none"}
          _groupHover={{
            display: ctx.product.line_sheet_id ? "block" : "none",
          }}
          top="50%"
          left={"50%"}
          transform={"translate(-50%, -50%)"}
        >
          <Button
            paddingX={"8px"}
            paddingY={"4px"}
            display={"flex"}
            gap={"4px"}
            background={"#FCFCFB"}
            border={`1px solid #8F8F8C`}
            borderRadius={"4px"}
            height={"28px"}
            role={"group"}
            _hover={{
              borderColor: "#1272EF",
              "> *": { color: "#1272EF" },
            }}
            _active={{
              background: "#FCFCFB",
              borderColor: "#00417D",
              "> *": { color: "#00417D" },
            }}
            onClick={() => {
              ctx.product.line_sheet_id &&
                navigateToLineSheetPage(ctx.product.sku);
            }}
          >
            {!ctx.product.line_sheet_id && (
              <Box color={"#8F8F8C"}>
                <SVG src={"/icons-v1_1/bell.svg"} width={16} height={16} />
              </Box>
            )}
            <Text textStyle={"body-sm-bold"} color={"#444444"}>
              {!ctx.product.line_sheet_id
                ? "알림 신청"
                : tTitle("common:navigation.stock")}
            </Text>
          </Button>
          {/* 단품오더 재고가 있는 상품(in_valid_api_order)만 패스픽 상품 찾기로 보낸다 —
              같은 SKU 를 검색어로 넘긴다. 예전 단품오더 버튼과 같은 자리·같은 기준. */}
          {ctx.product.in_valid_api_order && (
            <Button
              paddingX={"8px"}
              paddingY={"4px"}
              display={"flex"}
              gap={"4px"}
              background={"#FCFCFB"}
              border={`1px solid #8F8F8C`}
              borderRadius={"4px"}
              height={"28px"}
              role={"group"}
              marginTop={"10px"}
              _hover={{
                borderColor: "#1272EF",
                "> *": { color: "#1272EF" },
              }}
              _active={{
                background: "#FCFCFB",
                borderColor: "#00417D",
                "> *": { color: "#00417D" },
              }}
              onClick={() => {
                navigateToPathPickSearchProductsPage(ctx.product.sku);
              }}
            >
              <Text textStyle={"body-sm-bold"} color={"#444444"}>
                {tTitle("common:navigation.path_pick")}
              </Text>
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

function ProductCardName() {
  const ctx = useContext(CardContext)!;
  return (
    <Text textStyle={"body-sm-bold"} isTruncated={true}>
      {ctx.product.brand_name}
    </Text>
  );
}

function ProductCardSku() {
  const ctx = useContext(CardContext)!;
  const { info: showInfo, error: showError } = useAppToasts();

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(ctx.product.sku);
      showInfo("SKU 복사됨");
    } catch (err) {
      showError(err);
    }
  };

  return (
    <Text
      textStyle={"body-sm-regular"}
      noOfLines={2}
      minH={"40px"}
      cursor={"pointer"}
      onClick={handleCopy}
      _hover={{ color: "#1272EF" }}
    >
      {ctx.product.sku}
    </Text>
  );
}

function ProductCardPrice() {
  const ctx = useContext(CardContext)!;
  const textRef = useRef<HTMLParagraphElement>(null);
  const { tTitle } = useI18nHelper();

  useEffect(() => {
    if (!textRef.current) return;
    const ro = new ResizeObserver((entries) => {
      if (!textRef.current) return;

      const height = entries[0].contentRect.height;
      if (height === 16) {
        textRef.current.style.textAlign = "end";
      } else {
        textRef.current.style.textAlign = "start";
      }
    });
    ro.observe(textRef.current);

    return () => ro.disconnect();
  }, []);

  return (
    <Flex flexDirection={"column"} gap={"8px"}>
      <Flex justifyContent={"space-between"} flexWrap={"wrap"} ref={textRef}>
        <Text textStyle={"caption-regular"} whiteSpace={"nowrap"}>
          {tTitle("retail")}
        </Text>
        <Text textStyle={"caption-bold"} width={"92px"}>
          ₩{ctx.product.price.toLocaleString()}
        </Text>
      </Flex>
      {ctx.showOrderable && (
        <Flex gap={"6px"}>
          {ctx.product.in_valid_line_sheet && (
            <AppLabel
              nameKey={tTitle("common:line_sheet_set.types.stock")}
              level={"LIGHT"}
              isToUpper={false}
            />
          )}
          {ctx.product.in_valid_api_order && (
            <AppLabel
              nameKey={tTitle("common:navigation.path_pick")}
              level={"LIGHT"}
              isToUpper={false}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
}

BestSellingProductCard.Image = ProductCardImage;
BestSellingProductCard.Name = ProductCardName;
BestSellingProductCard.Sku = ProductCardSku;
BestSellingProductCard.Price = ProductCardPrice;

export default BestSellingProductCard;
